import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Input, CheckboxGroup } from "formsy-react-components";
import { Row, Col } from "react-bootstrap";
import zxcvbn from "zxcvbn";
import orderBy from "lodash/orderBy";
import find from "lodash/find";
import moment from "moment";
import Formsy from "formsy-react";

import { formatDate } from "../../helpers/date";

import DropdownSearch from "../common/DropdownSearch";

const UserForm = ({ user, users, departments, roles, codes, ...props }) => {
  const employmentStatusList =
    codes && codes.filter((code) => code.field === "employment_status");

  const [canSubmit, setCanSubmit] = useState(false);
  const [employment_status, setStatus] = useState(null);
  const [department, setDepartment] = useState(null);
  const [subDepartment, setSubDepartment] = useState(null);
  const [manager, setManager] = useState(null);
  const [strength, setStrength] = useState(-1);
  const [password, setPassword] = useState("");

  const minStrength = 4;

  const enableButton = () => {
    if (!props.adding || strength >= minStrength) {
      setCanSubmit(true);
    }
  };

  const disableButton = () => {
    setCanSubmit(false);
  };

  const cancel = () => {
    props.onCancel();
  };

  const getPasswordStrength = (pw) => {
    const str = zxcvbn(pw).score;
    const re = new RegExp(/[!@#$%^&*(),.?":{}|<>]/gi);
    if (re.test(pw)) {
      return str;
    }
    return str - 1;
  };

  const handlePasswordChange = (field, value) => {
    setPassword(value);
    const result = getPasswordStrength(value);
    setStrength(result);
  };

  const generateUserOptions = () => {
    // find all users with supervisor role or above for manager field
    const userOptions = users
      .filter(
        (u) =>
          u.roles.some((role) => role.number >= 3000) && u._id !== user?._id
      )
      .map((u) => ({
        value: u._id,
        label: u.full_name
      }));

    return orderBy(userOptions, "label");
  };

  const submit = (model) => {
    const data = {
      ...model,
      full_name: `${model.first_name} ${model.last_name}`
    };
    if (data.hire_date) {
      data.hire_date = moment(data.hire_date).toDate();
    }
    if (data.termination_date) {
      data.termination_date = moment(data.termination_date).toDate();
    }
    const selectedDepartment = departments.find(
      (dep) => dep._id === data.department
    );
    if (selectedDepartment && !selectedDepartment.sub_departments?.length) {
      data.sub_department = null;
    }
    props.onSave(data);
  };

  const subDepartments = useMemo(() => {
    const current = department || user.department;
    const dep = departments.find((d) => d._id === current);
    return dep?.sub_departments || [];
  }, [department, user, departments]);

  return (
    <Formsy
      className="vertical form"
      onValidSubmit={submit}
      onValid={enableButton}
      onInvalid={disableButton}
    >
      <Row>
        <Col md={12} xs={12}>
          <fieldset>
            <legend>User Roles</legend>
          </fieldset>
        </Col>
        <Col md={12} xs={12} className="roles">
          <CheckboxGroup
            name="roles"
            disabled={!props.editing}
            value={user.roles}
            options={orderBy(roles, "number", "desc").map((role) => ({
              value: role._id,
              label: role.name
            }))}
          />
        </Col>
        <Col md={12} xs={12}>
          <fieldset>
            <legend>Employee Info</legend>
          </fieldset>
        </Col>
        <Col md={5} xs={12}>
          <Input
            required
            name="first_name"
            label="First Name"
            disabled={!props.editing}
            placeholder="First Name"
            value={user.first_name || ""}
            validations={{ maxLength: 250 }}
            validationErrors={{
              maxLength: "You can not type in more than 250 characters"
            }}
          />
        </Col>
        <Col md={2} xs={12}>
          <Input
            name="middle_initial"
            label="Middle Initial"
            disabled={!props.editing}
            placeholder="Middle Initial"
            value={user.middle_initial || ""}
            validations={{ maxLength: 50 }}
            validationErrors={{
              maxLength: "You can not type in more than 50 characters"
            }}
          />
        </Col>
        <Col md={5} xs={12}>
          <Input
            required
            name="last_name"
            label="Last Name"
            disabled={!props.editing}
            placeholder="Last Name"
            value={user.last_name || ""}
            validations={{ maxLength: 250 }}
            validationErrors={{
              maxLength: "You can not type in more than 250 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            required
            name="email"
            label="Email"
            placeholder="Email"
            value={user.email || ""}
            disabled={!props.editing}
            validations={{
              isEmail: true,
              maxLength: 250,
              isExists: (values, value) => {
                const foundUser = find(users, { email: value });
                const alreadyExists = foundUser && foundUser._id !== user._id;
                return !alreadyExists;
              }
            }}
            validationErrors={{
              isEmail: "This is not a valid email",
              isExists: "There is a user with this email.",
              maxLength: "You can not type in more than 250 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            name="job_title"
            label="Title"
            placeholder="Title"
            value={user.job_title}
            disabled={!props.editing}
            validations={{ maxLength: 250 }}
            validationErrors={{
              maxLength: "You can not type in more than 250 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            required
            name="tech_id"
            label="Tech id"
            placeholder="Tech id"
            value={user.tech_id}
            disabled={!props.editing}
            validations={{ maxLength: 50 }}
            validationErrors={{
              maxLength: "You can not type in more than 50 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <DropdownSearch
            required
            name="employment_status"
            label="Employment Status"
            onChange={(n, selected) => setStatus(selected.value)}
            value={employment_status || user.employment_status}
            disabled={!props.editing}
            data={orderBy(
              [
                ...employmentStatusList.map((status) => ({
                  value: status._id,
                  label: status.label
                }))
              ],
              "label"
            )}
          />
        </Col>
        <Col md={4} xs={12}>
          <DropdownSearch
            required
            name="department"
            label="Department"
            onChange={(n, selected) => setDepartment(selected.value)}
            value={department || user.department}
            disabled={!props.editing}
            data={orderBy(
              [
                ...departments.map((dep) => ({
                  value: dep._id,
                  label: dep.description
                }))
              ],
              "label"
            )}
          />
        </Col>
        {!!subDepartments.length && (
          <Col md={4} xs={12}>
            <DropdownSearch
              name="sub_department"
              label="Sub Department"
              onChange={(n, selected) => setSubDepartment(selected.value)}
              value={subDepartment || user.sub_department}
              disabled={!props.editing}
              data={orderBy(
                [
                  ...subDepartments.map((dep) => ({
                    value: dep._id,
                    label: dep.description
                  }))
                ],
                "label"
              )}
            />
          </Col>
        )}
        <Col md={4} xs={12}>
          <DropdownSearch
            required
            name="manager"
            label="Manager"
            onChange={(n, selected) => setManager(selected.value)}
            value={manager || user.manager}
            disabled={!props.editing}
            data={[...generateUserOptions()]}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            required
            type="date"
            name="hire_date"
            label="Hire Date"
            disabled={!props.editing}
            value={
              user.hire_date ? formatDate(user.hire_date, "yyyy-MM-dd") : ""
            }
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            type="date"
            name="termination_date"
            label="Termination Date"
            disabled={!props.editing}
            value={
              user.termination_date
                ? formatDate(user.termination_date, "yyyy-MM-dd")
                : ""
            }
          />
        </Col>
        {props.adding && (
          <Col md={12} xs={12}>
            <fieldset>
              <legend>Password</legend>
            </fieldset>
          </Col>
        )}

        {props.adding && (
          <Col md={6} xs={12}>
            <Input
              required
              type="password"
              name="password"
              label="Password"
              placeholder="New Password"
              value={password}
              onChange={handlePasswordChange}
              disabled={!props.editing}
            />
          </Col>
        )}
        {props.adding && (
          <Col md={6} xs={12}>
            <Input
              required
              type="password"
              name="verifyPassword"
              label="Verify Password"
              placeholder="Verify Password"
              validations="equalsField:password"
              validationError="Passwords do not match."
              disabled={!props.editing}
            />
          </Col>
        )}
        {props.adding && (
          <Col xs={12}>
            <div className="strength-meter mt-2">
              <div className="strength-meter-fill" data-strength={strength} />
            </div>
          </Col>
        )}
      </Row>
      <Row>
        <Col md={12} xs={12} className="footer">
          <button className="btn btn-cancel" type="button" onClick={cancel}>
            Cancel
          </button>
          <input
            className="btn btn-success"
            type="submit"
            disabled={!canSubmit || props.saving}
            value={props.saving ? "Saving... " : "Save"}
          />
        </Col>
      </Row>
    </Formsy>
  );
};

UserForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  editing: PropTypes.bool.isRequired,
  user: PropTypes.object,
  codes: PropTypes.array.isRequired,
  departments: PropTypes.array,
  users: PropTypes.array,
  roles: PropTypes.array,
  adding: PropTypes.bool
};

export default UserForm;
